/* AN INSTALLATION'S SEVERAL DELIVERABLES.

   Panels that meet — the two of a corner screen, the grid of a video wall —
   are several files, because they go to several media players, but they are ONE
   export. That distinction is the whole design of this file:

   - one charge, not one per panel. resolveWatermark() spends on each call, so
     it is called once and the answer used for all of them. Priced as its own
     thing (EXPORT_COST.videoPanels) rather than as a single video, because it
     renders and encodes a file per panel — several exports' worth of work
     delivered as one.
   - one ZIP, not several downloads. Put a panel on the wrong face and the
     illusion is inside out, so they travel together with a README naming which
     is which. Mixing them up is the likeliest mistake on site and it costs
     nothing to prevent.
   - one analytics event carrying the panel count, so the credits-to-exports
     ratio stays readable rather than showing N exports for one spend.

   Written for a corner's two panels and now also used for an array's N, which
   the loops below always were: they map over a plan rather than naming a left
   and a right. The single-screen path in png.js is untouched and remains the
   common case.

   THE ONE RULE NOT TO BREAK is a single settleAt per instant with every panel
   rendered inside it. scene/video.js advances a decoder cursor and asserts the
   instant it is handed matches the pass it was built for; a second settle for
   the same frame trips it, and that cursor is what catches a silently
   one-frame-off video. A second renderer.render advances no decoder, so all
   panels come from the one settled state. scene/video.js is untouched. */
import { beginFrames, renderPanel } from '../export/frame.js';
import { createVideoSource } from './video-source.js';
import { CancelledError, canEncodeAt, encodeSizeFor, motionBlurSamples, subOffset } from '../export/mp4.js';
import { resolveWatermark, stampWatermark } from '../export/png.js';
import { zipStore } from '../export/zip.js';
import { cornerRenderable, tileCam } from '../scene/cameras.js';
import { cornerCam, flatCam } from '../scene/context.js';
import { panelDescs } from '../scene/panels.js';
import { applyAnimation, frameCount, frameDur, settleAt } from '../state/timeline.js';
import { beginVideoPass } from '../scene/video.js';
import { S } from '../state/store.js';
import { track } from '../services/analytics.js';
import { MAX_TILES } from '../core/doc.js';
import { EXPORT_COST, canExportVideoFiles, canUseScreenArray } from '../services/limits.js';
import { contactSheetHTML } from './contact-sheet.js';
import { exportName } from './filename.js';
import { toast } from '../ui/toast.js';

/* THE WHOLE WALL AS ONE FILE.

   An LCD video wall's deliverable is one file per screen, because that is what the
   players take. This is the other thing people need from it: the wall as a
   single picture, to show someone. It is a REVIEW file, not a deliverable —
   capped so the long side is 1920 rather than rendered at the wall's own
   resolution, which for a 3 x 2 of 1080p panels is 5823 px across and is both
   enormous and no use to anyone as a video.

   It goes through the ordinary single-screen path, and that is not a
   coincidence: S.screen IS the wall (see store.js's screenArray), so exportPNG
   and exportMP4 already frame exactly this. The only thing wanted from here is
   the size.

   The margins are IN it, and should be: this is a picture of how the wall will
   look, bezels and all, and it follows whatever you set them to on screen. The
   per-screen files cannot contain one either way. */
export const WALL_LONG_SIDE = 1920;
export function wholeWallSize(){
  const w = S.screen.w, h = S.screen.h;
  const k = WALL_LONG_SIDE / Math.max(w, h);
  // even, because H.264 refuses odd dimensions and the still should match it
  const ev = n => Math.max(2, Math.round(n / 2) * 2);
  return { w: ev(w * k), h: ev(h * k) };
}

/** Every panel, as it is described to the person installing it.
 *  Returns null for a plain single screen, which png.js already handles. */
export function panelPlan(){
  const a = S.screenArray;
  if(a){
    if(a.mode === 'free')
      /* The screen's OWN key, not a derived one: a free layout has no row or
         column to read it back from, so the label typed in the panel (or the
         A/B/C a new screen gets by default) is the only name that means
         anything on site. Its own resW/resH too — panelDescs already carries
         them per screen, because a free wall has no one pitch to read a.res
         for. */
      return panelDescs().map((d, i) => ({
        key: d.key, desc: d, cam: tileCam(i),
        resW: d.resW, resH: d.resH, w: d.w,
        label: `screen ${d.key}`
      }));
    /* Row/column, not "left"/"right": on a wall those words stop meaning
       anything, and r2c3 is what the installer can check against the physical
       grid without counting. One-based for the same reason. */
    return panelDescs().map((d, i) => ({
      key: `R${d.row + 1}C${d.col + 1}`,
      desc: d, cam: tileCam(i),
      resW: a.res.w, resH: a.res.h, w: d.w,
      label: `row ${d.row + 1}, column ${d.col + 1}`
    }));
  }
  const ds = panelDescs();
  if(ds.length < 2) return null;                 // an ordinary single screen
  return [
    { key: 'Left',  desc: ds[0], cam: flatCam,   resW: S.res.w,
      resH: S.res.h, w: S.screen.w, label: 'left' },
    { key: 'Right', desc: ds[1], cam: cornerCam, resW: S.cornerPanel.resW,
      resH: S.res.h, w: ds[1].w, label: 'right' }
  ];
}

/* Every README this app writes ends the same way, and it is signed HERE rather
   than in each branch below so a third kind of installation cannot ship without
   it — which is exactly how the corner and the array would have drifted apart.
   The trailing blank line is deliberate: a text file whose last line has no
   newline reads as truncated in some editors. */
const SITE = 'www.illusio3d.com';
export const sign = lines => lines.concat(['Made with Illusio 3D  ·  ' + SITE, '']).join('\n');

/* Which file goes where, said in words. Panels are otherwise distinguishable
   only by aspect ratio, and on site — months later, by someone who was not
   here — that is not enough. */
function readme(plan){
  const a = S.screenArray;
  if(a && a.mode === 'free'){
    const lines = [
      `ILLUSIO 3D — LCD video wall, ${plan.length} screens placed freely`,
      '',
      'No fixed grid or margin: each screen was placed and sized by hand, so',
      'the layout below is what was actually built, not a formula.',
      '',
      'The scene continues BEHIND every gap exactly as it does behind a bezel:',
      'each file shows only what its own screen can see from the viewer’s',
      'position, so hang them at any other spacing or size and the picture will',
      'not line up.',
      '',
      'Files are named by the letter each screen was given:', ''
    ];
    for(const p of plan)
      lines.push(`    ${fileName(p)}  —  ${p.label}, ${p.resW} x ${p.resH} px, `
        + `${fmt(p.desc.w)} x ${fmt(p.desc.h)} m`);
    lines.push('',
      'All of them were rendered for one viewer standing in one place. Moving',
      'a screen, or mirroring any of them, breaks the illusion rather than',
      'reversing it.', '');
    return sign(lines);
  }
  if(a){
    const lines = [
      `ILLUSIO 3D — LCD video wall, ${a.cols} across x ${a.rows} down (${plan.length} screens)`,
      '',
      `Each screen is ${a.res.w} x ${a.res.h} px, ${fmt(a.tile.w)} x ${fmt(a.tile.h)} m.`,
      a.gap > 0
        ? `They hang with a ${fmt(a.gap)} m gap between them, and the render ASSUMES`
        : 'They hang edge to edge, with no gap between them.',
    ];
    if(a.gap > 0) lines.push(
      'that gap. The scene continues BEHIND the bezels: each file shows only what',
      'its own screen can see from the viewer’s position, so the parts that fall in',
      'a gap are not in any file. Hang them at any other spacing and the picture',
      'will not line up.');
    lines.push('',
      'Files are named by row and column, counting from the TOP-LEFT as seen by',
      'the viewer:', '');
    for(const p of plan) lines.push(`    ${fileName(p)}  —  ${p.label}`);
    lines.push('',
      'All of them were rendered for one viewer standing in one place. Swapping',
      'two screens, or mirroring any of them, breaks the illusion rather than',
      'reversing it.', '');
    return sign(lines);
  }
  const h = S.res.h;
  return sign([
    'ILLUSIO 3D — corner screen, two panels',
    '',
    `${fileName(plan[0])}`,
    `    the LEFT panel, ${plan[0].resW} x ${h} px.`,
    '    Its RIGHT edge is the corner.',
    '',
    `${fileName(plan[1])}`,
    `    the RIGHT panel, ${plan[1].resW} x ${h} px.`,
    '    Its LEFT edge is the corner.',
    '',
    'The two share that edge. Both were rendered for one viewer standing where',
    'the reference photo was taken — swapping them, or mirroring either one,',
    'breaks the illusion rather than reversing it.',
    ''
  ]);
}
const fmt = n => (Math.round(n * 1000) / 1000).toString();
const fileName = (p, ext = 'png') =>
  exportName({ kind: 'Panel_' + p.key, spec: `${p.resW}x${p.resH}`, ext });

/* What the ZIP is called, and what the toast says — both have to name an array
   by its grid rather than by two panel widths. */
const setName = (plan, fps) => {
  const a = S.screenArray;
  if(a && a.mode === 'free')
    /* No cols x rows to name it by, and plan[0]'s own resolution says nothing
       about the other N-1 screens — a free wall's one honest number is the
       count, and its overall size in the wall's own bounding box. */
    return exportName({ kind: `Wall_Free_${plan.length}`,
                         spec: `${S.res.w}x${S.res.h}`, fps, ext: 'zip' });
  if(a)
    return exportName({ kind: `Wall_${a.cols}x${a.rows}`,
                         spec: `${plan[0].resW}x${plan[0].resH}`, fps, ext: 'zip' });
  return exportName({ kind: 'Corner',
                       spec: `${plan[0].resW}+${plan[1].resW}x${S.res.h}`, fps, ext: 'zip' });
};

/**
 * Both panels as PNGs, in one ZIP.
 * @returns {Promise<{ok:boolean, reason?:string}>}
 */
export async function exportPNGPanels(){
  const plan = panelPlan();
  if(!plan) return { ok: false, reason: 'no panels' };
  /* A wall's several files are the Advanced deliverable, so the stills are
     gated where the video already was — otherwise the licence is bypassed by
     opening a project that has an array rather than making one. A CORNER is
     not gated and never was: two panels have always been part of the base
     product, and quietly locking them here would be taking something away. */
  if(S.screenArray){
    const gate = canUseScreenArray();
    if(!gate.ok) return { ok: false, reason: gate.reason };
  }
  /* An array is coplanar, so it is never edge-on and this cannot apply to it —
     the check exists for a corner's folded second panel. */
  if(!S.screenArray && !cornerRenderable())
    return { ok: false, reason: 'The viewpoint is almost edge-on to the second panel, so it cannot be rendered from here. Move the viewer round, or re-mark the corner.' };

  const pass = beginFrames();
  let shots;
  try{
    /* ONE settle for every panel. The scene is already at this instant after
       it, and a second render advances no decoder — which is what keeps the
       video path's per-instant contract intact (see scene/video.js). */
    await settleAt(S.playhead);
    shots = plan.map(p => {
      const cv = renderPanel(p.desc, null, p.resW, p.resH, p.cam);
      // copied out immediately: renderPanel reuses the renderer's own canvas,
      // so the next panel would otherwise overwrite this one
      const keep = document.createElement('canvas');
      keep.width = cv.width; keep.height = cv.height;
      keep.getContext('2d').drawImage(cv, 0, 0);
      return keep;
    });
  } finally {
    pass.end();
  }

  const mark = await resolveWatermark(undefined, EXPORT_COST.still);   // stills are free and clean
  /* One mark size across the set, taken from the FIRST panel. Panels may differ
     in width, so sizing each off its own frame put a visibly smaller logo on
     the narrower one — several different marks on one continuous piece. Matches the live preview, which is the whole reason
     export/watermark.js is one shared geometry. */
  const markRef = { w: shots[0].width, h: shots[0].height };
  const files = [];
  for(let i = 0; i < plan.length; i++){
    if(mark) await stampWatermark(shots[i], markRef);
    const blob = await new Promise(res => shots[i].toBlob(res, 'image/png'));
    files.push({ name: fileName(plan[i]), blob });
  }
  files.push({ name: 'README.txt',
               blob: new Blob([readme(plan)], { type: 'text/plain' }) });

  const zip = await zipStore(files);
  download(zip, setName(plan, 0));
  /* "Each" is a claim about the whole set, so a free wall — where two screens
     can be different sizes — has to name each one's own dimensions rather
     than borrow the first screen's and misstate the rest. */
  toast(S.screenArray?.mode === 'free'
    ? `Exported ${plan.length} screens — ${plan.map(p => p.resW + '×' + p.resH).join(', ')}, zipped together.`
    : S.screenArray
    ? `Exported ${plan.length} screens — ${plan[0].resW}×${plan[0].resH} each, zipped together.`
    : `Exported both panels — ${plan[0].resW}×${plan[0].resH} and ${plan[1].resW}×${plan[1].resH}, zipped together.`);
  track('export_png', { w: plan[0].resW, h: plan[0].resH, panels: plan.length });
  return { ok: true };
}

/**
 * Both panels as MP4s, in one ZIP.
 *
 * The load-bearing detail is ONE settle per instant, two renders inside it.
 * scene/video.js's settleVideos advances a cursor and asserts the instant it is
 * handed matches the one the decode pass was built for — that cursor exists to
 * catch a silently one-frame-off video, which is the kind of defect nobody
 * notices until a client does. Calling settleAt twice per output frame would
 * trip it on the very first frame. It does not need relaxing: the scene is
 * already at time t after one settle, and a second renderer.render advances no
 * decoder, so both panels are rendered from the one settled state. scene/video.js
 * is untouched by this feature.
 */
export async function exportMP4Panels(opts = {}){
  /* The same backstop as exportMP4 — a video file needs a credit pack or a
     licence, whether it is one screen, a corner's pair or a wall. */
  const access = canExportVideoFiles();
  if(!access.ok) return { ok: false, reason: access.reason };
  const plan = panelPlan();
  if(!plan) return { ok: false, reason: 'no panels' };
  if(!S.screenArray && !cornerRenderable())
    return { ok: false, reason: 'The viewpoint is almost edge-on to the second panel, so it cannot be rendered from here. Move the viewer round, or re-mark the corner.' };

  const mb = await import('mediabunny');
  const { Output, Mp4OutputFormat, BufferTarget } = mb;

  const fps = S.timeline.fps, total = frameCount();
  const samples = motionBlurSamples();
  const sizes = plan.map(p => encodeSizeFor(p.resW, p.resH));

  /* Every size checked before anything is rendered — a set that encodes the
     first panel and then discovers the fifth is refused has spent a credit and
     twenty seconds for nothing. */
  for(let i = 0; i < plan.length; i++)
    if(!(await canEncodeAt(sizes[i].w, sizes[i].h)))
      return { ok: false, reason:
        `This browser will not encode H.264 at ${sizes[i].w} × ${sizes[i].h}, which the ` +
        `${plan[i].label} panel needs. Reduce the output resolution.` };

  /* One encoder per panel, and mediabunny's BufferTarget holds each finished
     file in memory until the ZIP is written — so the ceiling on an array is the
     browser's, not the geometry's. doc.js's MAX_TILES caps it at the point the
     grid is set, which is where it can still be explained; this is the backstop
     for a project that arrived carrying more. */
  if(plan.length > MAX_TILES)
    return { ok: false, reason:
      `${plan.length} screens is more than this can encode at once. Reduce the array to ${MAX_TILES} or fewer.` };

  const instants = [];
  for(let f = 0; f < total; f++){
    if(samples > 1) for(let i = 0; i < samples; i++) instants.push((f + subOffset(i, samples)) * frameDur());
    else instants.push(f * frameDur());
  }

  /* Priced as a multi-panel video: it renders and encodes one file per panel,
     so it is several exports' worth of work delivered as one. */
  const watermark = await resolveWatermark(opts.watermark, EXPORT_COST.videoPanels);
  /* One seed for the whole export, so both panels move on the same beat — see
     export/watermark.js. Each still places within its own frame, so the two do
     not land in the same spot on a pair of different-sized panels. */
  const markSeed = (Math.random() * 0x7fffffff) | 0;
  const playheadWas = S.playhead;
  const pass = beginFrames();
  const targets = plan.map((p, i) => ({
    ...p, w: sizes[i].w, h: sizes[i].h,
    canvas: document.createElement('canvas'),
    sub: samples > 1 ? document.createElement('canvas') : null
  }));
  for(const t of targets) if(samples > 1){ t.canvas.width = t.w; t.canvas.height = t.h; }

  /* Before the try below, so a failure here has to end the pass itself: the
     pass holds the live preview (state/bus.js holdRendering) until it ends. */
  let vpass;
  try { vpass = await beginVideoPass(instants); } catch (e) { pass.end(); throw e; }
  const outs = [];
  try{
    for(const t of targets){
      const out = new Output({ format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
                               target: new BufferTarget() });
      const video = createVideoSource(mb, t.canvas);   // limited-range BT.709, see export/video-source.js
      out.addVideoTrack(video.source, { frameRate: fps });
      await out.start();
      outs.push({ out, video, t });
    }

    for(let f = 0; f < total; f++){
      if(opts.cancel?.aborted) throw new CancelledError();

      if(samples > 1){
        for(const o of outs) o.t.canvas.getContext('2d').clearRect(0, 0, o.t.w, o.t.h);
        for(let i = 0; i < samples; i++){
          await settleAt((f + subOffset(i, samples)) * frameDur());   // ONCE per sub-sample
          for(const o of outs){
            renderPanel(o.t.desc, o.t.sub, o.t.w, o.t.h, o.t.cam);
            const ctx = o.t.canvas.getContext('2d');
            ctx.globalAlpha = 1 / (i + 1);
            ctx.drawImage(o.t.sub, 0, 0);
            ctx.globalAlpha = 1;
          }
        }
      }else{
        await settleAt(f * frameDur());                               // ONCE per frame
        for(const o of outs) renderPanel(o.t.desc, o.t.canvas, o.t.w, o.t.h, o.t.cam);
      }

      /* Same one-size rule as the PNG pair above — the first panel's frame
         sizes the mark on both. */
      const vidRef = { w: outs[0].t.canvas.width, h: outs[0].t.canvas.height };
      for(const o of outs){
        if(watermark) await stampWatermark(o.t.canvas, vidRef, { seconds: f / fps, seed: markSeed });
        await o.video.add(f / fps, 1 / fps);    // the encoder's own backpressure
      }
      opts.onProgress?.(f + 1, total);
    }

    const files = [];
    for(const o of outs){
      await o.out.finalize();
      files.push({ name: fileName(o.t, 'mp4'),
                   blob: new Blob([o.out.target.buffer], { type: 'video/mp4' }) });
    }
    files.push({ name: 'README.txt', blob: new Blob([readme(plan)], { type: 'text/plain' }) });
    /* And a page that SHOWS what the README describes. Named index.html
       because that is the file a person double-clicks after unzipping without
       being told to. It references the clips beside it, so it is written with
       the same fileName() the entries above were — one source for both, rather
       than two that agree today. */
    files.push({
      name: 'index.html',
      blob: new Blob([await contactSheetHTML(plan, p => fileName(p, 'mp4'),
                                             { fps, seconds: total / fps })],
                     { type: 'text/html' })
    });
    download(await zipStore(files), setName(plan, fps));
    toast(S.screenArray
      ? `Exported ${plan.length} screens — ${total} frames each at ${fps}fps, zipped together.`
      : `Exported both panels — ${total} frames each at ${fps}fps, zipped together.`);
    track('export_mp4', { watermarked: watermark, w: plan[0].resW, h: plan[0].resH,
                          fps, frames: total, panels: plan.length });
    return { ok: true };
  } finally {
    await vpass.end();
    pass.end();
    applyAnimation(playheadWas);
  }
}

function download(blob, name){
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}
